/**
 * Immutable document operations and undo/redo history.
 * Docs are treated as immutable values; every op returns a new doc.
 */
import type { Item, MapDoc } from '../../shared/doc.ts';

export function newId(): string {
  // 20 chars of URL-safe randomness (~119 bits).
  const bytes = new Uint8Array(15);
  crypto.getRandomValues(bytes);
  let s = '';
  for (const b of bytes) s += b.toString(16).padStart(2, '0');
  return s.slice(0, 20);
}

export function addItem(doc: MapDoc, item: Item): MapDoc {
  return { ...doc, items: [...doc.items, item] };
}
export function removeItems(doc: MapDoc, ids: ReadonlySet<string>): MapDoc {
  return { ...doc, items: doc.items.filter((it) => !ids.has(it.id)) };
}
export function updateItem<T extends Item>(doc: MapDoc, id: string, fn: (it: T) => T): MapDoc {
  let changed = false;
  const items = doc.items.map((it) => {
    if (it.id !== id) return it;
    const next = fn(it as T);
    if (next !== it) changed = true;
    return next;
  });
  return changed ? { ...doc, items } : doc;
}
export function replaceItem(doc: MapDoc, item: Item): MapDoc {
  return updateItem(doc, item.id, () => item);
}
export function getItem(doc: MapDoc, id: string): Item | undefined {
  return doc.items.find((it) => it.id === id);
}
export function setDoc<K extends keyof MapDoc>(doc: MapDoc, key: K, value: MapDoc[K]): MapDoc {
  return doc[key] === value ? doc : { ...doc, [key]: value };
}
/** Move an item to the top of the z-order. */
export function bringToFront(doc: MapDoc, id: string): MapDoc {
  const it = getItem(doc, id);
  if (!it) return doc;
  return { ...doc, items: [...doc.items.filter((x) => x.id !== id), it] };
}

export class History {
  #past: MapDoc[] = [];
  #future: MapDoc[] = [];
  #present: MapDoc;
  readonly limit: number;
  constructor(initial: MapDoc, limit = 200) {
    this.#present = initial;
    this.limit = limit;
  }
  get present(): MapDoc {
    return this.#present;
  }
  get canUndo(): boolean {
    return this.#past.length > 0;
  }
  get canRedo(): boolean {
    return this.#future.length > 0;
  }
  /** Commit a new state as an undoable step. No-op if identical. */
  commit(next: MapDoc): boolean {
    if (next === this.#present) return false;
    this.#past.push(this.#present);
    if (this.#past.length > this.limit) this.#past.shift();
    this.#future.length = 0;
    this.#present = next;
    return true;
  }
  /** Fold a change into the current undo step (typing into one field is one step). */
  amend(next: MapDoc): boolean {
    if (next === this.#present) return false;
    this.#future.length = 0;
    this.#present = next;
    return true;
  }
  /** Replace the present state without creating an undo step (e.g. remote sync). */
  reset(next: MapDoc): void {
    this.#present = next;
    this.#past.length = 0;
    this.#future.length = 0;
  }
  /** The state before the present one (the one undo would go back to), if any. */
  get previous(): MapDoc | undefined {
    return this.#past[this.#past.length - 1];
  }
  /** Take the last step back and forget it (no redo): it should never have been a step (a shot placed and left without its elevation). */
  withdraw(): MapDoc | null {
    const prev = this.#past.pop();
    if (!prev) return null;
    this.#present = prev;
    this.#future.length = 0;
    return prev;
  }
  undo(): MapDoc | null {
    const prev = this.#past.pop();
    if (!prev) return null;
    this.#future.push(this.#present);
    this.#present = prev;
    return prev;
  }
  redo(): MapDoc | null {
    const next = this.#future.pop();
    if (!next) return null;
    this.#past.push(this.#present);
    this.#present = next;
    return next;
  }
}
